import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import {
  Close,
  CheckCircle,
  VideoLibrary,
  Link,
  AutoAwesome,
  Security,
} from "@mui/icons-material";

interface NewsModalProps {
  open: boolean;
  onClose: () => void;
}

export function NewsModal({ open, onClose }: NewsModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const newsItems = [
    {
      icon: <AutoAwesome sx={{ color: "white" }} />,
      title: "Transcrição Automática com IA",
      description:
        "Nova funcionalidade revolucionária que transforma vídeos em transcrições precisas usando inteligência artificial.",
      features: [
        "Processamento automático de vídeos do YouTube, Facebook, Vimeo e Dailymotion",
        "Transcrição inteligente com reconhecimento de falantes",
        "Sincronização perfeita entre vídeo e texto",
        "Edição inline da transcrição para correções",
        "Geração automática de atas legislativas",
      ],
    },
    {
      icon: <VideoLibrary sx={{ color: "white" }} />,
      title: "Suporte Multiplataforma",
      description:
        "Agora você pode usar vídeos de qualquer plataforma principal para transcrição.",
      features: [
        "Detecção automática da plataforma do vídeo",
        "Ícones específicos para cada plataforma",
        "Player otimizado para cada tipo de vídeo",
        "Fallback inteligente para vídeos com restrições",
      ],
    },
    {
      icon: <Link sx={{ color: "white" }} />,
      title: "Validação Inteligente",
      description:
        "Sistema aprimorado que aceita qualquer formato de link de vídeo.",
      features: [
        "Suporte a pt-br.facebook.com e outros subdomínios",
        "Validação robusta de URLs",
        "Mensagens de erro claras e úteis",
        "Compatibilidade total com plataformas",
      ],
    },
    {
      icon: <Security sx={{ color: "white" }} />,
      title: "Experiência Personalizada",
      description:
        "Sistema inteligente que se adapta às suas necessidades e preferências.",
      features: [
        "Interface adaptativa por plataforma",
        "Detecção automática de problemas",
        "Links diretos quando embed não funciona",
        "Respeita suas configurações de privacidade",
      ],
    },
  ];

  const handleNext = () => {
    if (currentSlide < newsItems.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const currentItem = newsItems[currentSlide];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundColor: "white",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(59, 130, 246, 0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, px: 3, pt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight="700" color="#1F2937">
                Transcrição com IA
              </Typography>
              <Typography variant="body2" color="#6B7280" sx={{ mt: 0.5 }}>
                Nova funcionalidade importante para você
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#9CA3AF",
              "&:hover": {
                backgroundColor: "#F3F4F6",
                color: "#374151",
              },
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, px: 3, pb: 3 }}>
        <Box
          sx={{
            backgroundColor: "#F8FAFC",
            borderRadius: 3,
            p: 3,
            border: "1px solid #E2E8F0",
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: "#3B82F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                mr: 2,
              }}
            >
              {currentItem.icon}
            </Box>
            <Typography variant="h6" fontWeight="600" color="#1F2937">
              {currentItem.title}
            </Typography>
          </Box>

          <Typography
            variant="body1"
            color="#4B5563"
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            {currentItem.description}
          </Typography>

          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 2,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="600"
              color="#374151"
              sx={{ mb: 2 }}
            >
              Principais funcionalidades:
            </Typography>

            <List dense sx={{ py: 0 }}>
              {currentItem.features.map((feature, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: "#3B82F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle sx={{ color: "white", fontSize: 14 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "#4B5563",
                      sx: { lineHeight: 1.5 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        {/* Indicadores de progresso */}
        <Box display="flex" justifyContent="center" mb={3}>
          {newsItems.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: index === currentSlide ? "#3B82F6" : "#D1D5DB",
                mx: 0.5,
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>

        {/* Contador de slides */}
        <Typography
          variant="caption"
          textAlign="center"
          display="block"
          sx={{ mb: 3, color: "#6B7280", fontWeight: 500 }}
        >
          {currentSlide + 1} de {newsItems.length}
        </Typography>

        {/* Botões de navegação */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            onClick={handlePrevious}
            disabled={currentSlide === 0}
            sx={{
              color: currentSlide === 0 ? "#9CA3AF" : "#6B7280",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#F3F4F6",
                color: "#374151",
              },
            }}
          >
            Anterior
          </Button>

          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={`${currentSlide + 1}/${newsItems.length}`}
              size="small"
              sx={{
                backgroundColor: "#F3F4F6",
                color: "#6B7280",
                fontWeight: 500,
                border: "1px solid #E5E7EB",
              }}
            />
            <Button
              onClick={handleSkip}
              sx={{
                color: "#6B7280",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  color: "#374151",
                },
              }}
              size="small"
            >
              Pular
            </Button>
          </Box>

          <Button
            onClick={handleNext}
            variant="contained"
            sx={{
              backgroundColor: "#3B82F6",
              color: "white",
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#2563EB",
              },
            }}
          >
            {currentSlide === newsItems.length - 1 ? "Finalizar" : "Próximo"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
